import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'
import {
	AllFormErrors,
	CheckboxField,
	PublicStyledField,
	PublicStyledSelect,
} from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Country } from '#app/enums/country.ts'
import { Profession } from '#app/enums/profession.ts'
import { useLocale } from '#app/utils/i18n.tsx'
import { NewsletterSchema } from './schemas'

export function NewsletterForm({ id }: { id: string }) {
	const locale = useLocale()
	const [hasFocused, setHasFocused] = useState(false)
	const { t } = useTranslation('common')
	const fetcher = useFetcher()
	const [form, fields] = useForm({
		id,
		shouldValidate: 'onSubmit',
		shouldRevalidate: 'onInput',
		lastResult: fetcher.data,
		constraint: getZodConstraint(NewsletterSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: NewsletterSchema,
			})
		},
		onSubmit(event, context) {
			event.preventDefault()

			void fetcher.submit(context.formData, {
				action: '/resources/newsletter/subscribe',
				method: 'POST',
			})
		},
	})

	return (
		<div className="group z-30 mx-auto my-lg w-full max-w-[min(1560px,85vw)] md:my-xl md:px-sm">
			<fetcher.Form className={'flex flex-col gap-y-5'} {...getFormProps(form)}>
				<div className={'relative'}>
					<PublicStyledField
						placeholder={t('form.fields.email')}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							onKeyDown: () => {
								if (fields.email.value?.length && fields.email.valid)
									setHasFocused(true)
							},
							className: 'z-30 placeholder:lowercase',
						}}
						errors={fields.email.errors}
					/>

					<Button
						tabIndex={-1}
						type="submit"
						disabled={!form.valid}
						className={
							'absolute right-0 top-2 z-30 font-normal lowercase tracking-widest'
						}
						variant={'link'}
					>
						{t('actions.subscribe')}
					</Button>
				</div>

				<AnimatePresence>
					{hasFocused && (
						<motion.div
							className={'grid grid-cols-1 gap-x-3 gap-y-5 md:grid-cols-2'}
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								type: 'spring',
								stiffness: 300,
								damping: 30,
								delay: 0.25,
								duration: 0.6,
							}}
						>
							<PublicStyledField
								inputProps={{
									...getInputProps(fields.name, {
										type: 'text',
									}),
									autoComplete: 'first-name',
									className: 'placeholder:lowercase',
								}}
								placeholder={t('form.fields.name')}
							/>

							<PublicStyledField
								inputProps={{
									...getInputProps(fields.surname, {
										type: 'text',
									}),
									autoComplete: 'family-name',
									className: 'placeholder:lowercase',
								}}
								placeholder={t('form.fields.surname')}
							/>

							<PublicStyledSelect
								placeholder={`${t('form.fields.profession.industry')}`.toLowerCase()}
								items={Profession.getSelectOptions(locale)}
								meta={fields.profession}
							/>

							<PublicStyledSelect
								placeholder={`${t('form.fields.location.country')}`.toLowerCase()}
								items={Country.getSelectOptions(locale)}
								meta={fields.country}
							/>

							<CheckboxField
								labelProps={{
									children: t('form.disclaimer.message'),
								}}
								buttonProps={{
									...getInputProps(fields.privacy, {
										type: 'checkbox',
									}),
									id: 'newsletter-privacy',
								}}
								className={'col-span-full'}
							/>

							<AllFormErrors
								errors={form.allErrors}
								className={'col-span-full'}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</fetcher.Form>
		</div>
	)
}
